import React, { useEffect, useMemo, useState } from 'react';
import ModuleShell from '@/components/layout/ModuleShell';
import { useApi } from '@/contexts/AuthContext';
import { Download, FileText } from 'lucide-react';

const C = { ink: '#0D1B2A', slate: '#5C6B7A', line: '#E4E9EF', soft: '#F6F8FA' };

function fmtBytes(n) {
  if (n > 1048576) return `${(n / 1048576).toFixed(1)} MB`;
  if (n > 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

function Table({ lines }) {
  const rows = lines.filter((l) => !/^\|\s*-+/.test(l)).map((l) => l.replace(/^\||\|$/g, '').split('|').map((c) => c.trim()));
  return (
    <table className="text-xs my-3 border" style={{ borderColor: C.line }}>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} style={{ background: i === 0 ? C.soft : '#fff' }}>
            {r.map((c, j) => <td key={j} className="px-2 py-1 border align-top" style={{ borderColor: C.line, fontWeight: i === 0 ? 700 : 400 }}>{c}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function Markdown({ text }) {
  const blocks = useMemo(() => {
    const out = [];
    const lines = text.split('\n');
    let i = 0;
    while (i < lines.length) {
      const l = lines[i];
      if (l.startsWith('|')) {
        const t = [];
        while (i < lines.length && lines[i].startsWith('|')) t.push(lines[i++]);
        out.push({ k: 'table', lines: t });
        continue;
      }
      if (l.startsWith('```')) {
        const code = [];
        i++;
        while (i < lines.length && !lines[i].startsWith('```')) code.push(lines[i++]);
        i++;
        out.push({ k: 'code', text: code.join('\n') });
        continue;
      }
      const h = /^(#{1,4})\s+(.*)$/.exec(l);
      if (h) { out.push({ k: `h${h[1].length}`, text: h[2], id: h[2].toLowerCase().replace(/[^a-z0-9]+/g, '-') }); i++; continue; }
      if (/^\s*[-*]\s+/.test(l)) {
        const items = [];
        while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) items.push(lines[i++].replace(/^\s*[-*]\s+/, ''));
        out.push({ k: 'ul', items });
        continue;
      }
      if (l.trim() === '' || l.trim() === '---') { i++; continue; }
      const para = [];
      while (i < lines.length && lines[i].trim() !== '' && !lines[i].startsWith('|') && !lines[i].startsWith('#') && !/^\s*[-*]\s+/.test(lines[i]) && !lines[i].startsWith('```')) para.push(lines[i++]);
      out.push({ k: 'p', text: para.join(' ') });
    }
    return out;
  }, [text]);
  const inline = (s) => s.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, idx) => {
    if (part.startsWith('**')) return <strong key={idx}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('`')) return <code key={idx} className="px-1 rounded text-[11px]" style={{ background: C.soft }}>{part.slice(1, -1)}</code>;
    return part;
  });
  return (
    <div data-testid="platform-truth-document">
      {blocks.map((b, i) => {
        if (b.k === 'h1') return <h1 key={i} id={b.id} className="text-2xl font-bold mt-6 mb-2" style={{ color: C.ink }}>{inline(b.text)}</h1>;
        if (b.k === 'h2') return <h2 key={i} id={b.id} className="text-lg font-bold mt-8 mb-2 pb-1 border-b" style={{ color: C.ink, borderColor: C.line }}>{inline(b.text)}</h2>;
        if (b.k === 'h3') return <h3 key={i} id={b.id} className="text-sm font-bold mt-5 mb-1" style={{ color: C.ink }}>{inline(b.text)}</h3>;
        if (b.k === 'h4') return <h4 key={i} id={b.id} className="text-xs font-bold mt-4 mb-1 uppercase tracking-wide" style={{ color: C.slate }}>{inline(b.text)}</h4>;
        if (b.k === 'table') return <Table key={i} lines={b.lines} />;
        if (b.k === 'code') return <pre key={i} className="text-[11px] p-3 rounded my-2 overflow-x-auto" style={{ background: C.soft }}>{b.text}</pre>;
        if (b.k === 'ul') return <ul key={i} className="list-disc pl-5 my-2 text-sm space-y-1" style={{ color: C.ink }}>{b.items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ul>;
        return <p key={i} className="text-sm my-2 leading-relaxed" style={{ color: C.ink }}>{inline(b.text)}</p>;
      })}
    </div>
  );
}

function FilesCard({ files, onDownload }) {
  const groups = ['document', 'attachments', 'evidence'];
  return (
    <div className="p-5 rounded-lg border" style={{ borderColor: C.line, background: '#fff' }} data-testid="platform-truth-files">
      <div className="text-sm font-bold mb-3" style={{ color: C.ink }}>Attachments and evidence</div>
      {groups.map((g) => {
        const rows = files.filter((f) => f.folder === g);
        if (!rows.length) return null;
        return (
          <div key={g} className="mb-4">
            <div className="text-[11px] uppercase tracking-wide mb-1" style={{ color: C.slate }}>{g}</div>
            {rows.map((f) => (
              <div key={f.name} className="flex items-center justify-between py-1.5 border-b text-xs" style={{ borderColor: C.line }}>
                <div className="flex items-center gap-2 min-w-0"><FileText size={14} style={{ color: C.slate }} /><span className="truncate" style={{ color: C.ink }}>{f.name.split('/').pop()}</span><span style={{ color: C.slate }}>{fmtBytes(f.bytes)}</span></div>
                <button type="button" onClick={() => onDownload(f.name)} className="flex items-center gap-1 px-2 py-1 rounded border hover:bg-slate-50" style={{ borderColor: C.line, color: C.ink }} data-testid={`platform-truth-download-${f.name.split('/').pop().replace(/[^a-zA-Z0-9]+/g, '-')}`}>
                  <Download size={12} /> Download
                </button>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default function PlatformTruthPage() {
  const api = useApi();
  const [files, setFiles] = useState([]);
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/platform-truth/files').then((r) => setFiles(r.data.files || [])).catch((e) => setError(e?.response?.data?.detail || 'Could not load the file list'));
    api.get('/platform-truth/document').then((r) => setDoc(r.data)).catch((e) => setError(e?.response?.data?.detail || 'Document not written yet'));
  }, [api]);

  const saveBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };
  const download = async (name) => {
    const r = await api.get(`/platform-truth/files/${name}`, { responseType: 'blob' });
    saveBlob(r.data, name.split('/').pop());
  };
  const [zipping, setZipping] = useState(false);
  const downloadAll = async () => {
    setZipping(true);
    try {
      const r = await api.get('/platform-truth/download', { responseType: 'blob' });
      const cd = r.headers['content-disposition'] || '';
      const m = /filename="?([^";]+)"?/.exec(cd);
      saveBlob(r.data, m ? m[1] : 'platform_truth.zip');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not build the zip');
    } finally {
      setZipping(false);
    }
  };

  const content = (
    <div className="space-y-4" data-testid="platform-truth-page">
      <div className="flex items-start justify-between gap-4">
        <div className="text-xs" style={{ color: C.slate }}>
          Inventory generated from the code, the database, the logs and git on the preview build — not from memory. Status vocabulary: LIVE-WORKING · LIVE-PARTIAL · LIVE-BROKEN · BUILT-NOT-EXPOSED · MOCKED · DESIGNED-ONLY · RETIRED.
          {doc?.modified && <span data-testid="platform-truth-modified"> Document last written {new Date(doc.modified).toLocaleString()}.</span>}
        </div>
        <button type="button" onClick={downloadAll} disabled={zipping} className="shrink-0 flex items-center gap-2 px-3 py-2 rounded text-xs font-semibold text-white disabled:opacity-60" style={{ background: C.ink }} data-testid="platform-truth-download-all-btn">
          <Download size={14} /> {zipping ? 'Building zip…' : `Download everything (${files.length} files, zip)`}
        </button>
      </div>
      {error && <div className="text-xs p-3 rounded border" style={{ borderColor: '#F5C2C7', color: '#842029', background: '#F8D7DA' }} data-testid="platform-truth-error">{error}</div>}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className="xl:col-span-2 p-5 rounded-lg border" style={{ borderColor: C.line, background: '#fff' }}>
          {doc ? <Markdown text={doc.markdown} /> : <div className="text-sm" style={{ color: C.slate }}>Loading document…</div>}
        </div>
        <div><FilesCard files={files} onDownload={download} /></div>
      </div>
    </div>
  );
  const tabs = [{ id: 'truth', label: 'Platform Truth', content }];
  return <ModuleShell title="Platform Truth" subtitle="Complete platform inventory — Sections 1–20 and attachments" tabs={tabs} />;
}
